import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TeModal,
  TeContainer,
  TeContainerSlim,
  TeButton,
  TeForm,
  TeInputText,
  TeLabel,
  TeInputPassword,
  TeDropdown,
  TeDropdownOption,
  TeSegment,
  TeSegmentText,
  TeDivider,
} from '@topcon/tuicl-react';
import { Statuses } from '@topcon/tuicl/dist/common/component-types';
import {
  resetFormWithName,
  submitFormWithName,
} from '@topcon/tuicl/dist/components/form/form.utils';
import { useNotification } from '@topcon/topcon-utils';
import { TeChangeEvent, TeDropdownChangeEvent } from '@topcon/tuicl';

import { validateUrl } from '../../../utils/validation';
import { useAddIdentityProvider, useUpdateIdentityProvider } from './identityProviders.mutations';
import { useIdentityProvider } from './identityProviders.queries';
import {
  DomainBody,
  IdentityProviderBody,
  IdentityProviderPostBody,
} from '../../../api/generated/pap';
import { useErrorDrawer } from '../../../providers/ErrorDrawerContext/error-drawer.context';

interface Props {
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
  identityProvider: IdentityProviderBody | null;
  domains: DomainBody[];
  loadedDomains: boolean;
}

interface LoaderProps {
  id: string;
}

enum AddIdentityProviderFormNameKey {
  Name = 'name',
  Realm = 'realm',
  ClientId = 'clientId',
  ClientSecret = 'clientSecret',
  MetadataURL = 'metaDataURL',
  IssuerURL = 'issuerURL',
  AuthURL = 'authURL',
  TokenURL = 'tokenURL',
  UserInfoURL = 'userInfoURL',
  JwksURL = 'jwksURL',
  WellKnownConfigurationURL = 'wellKnownConfigurationURL',
}

const SamlFormKeys = [
  AddIdentityProviderFormNameKey.Name,
  AddIdentityProviderFormNameKey.Realm,
  AddIdentityProviderFormNameKey.MetadataURL,
];

const BasicFormKeys = [
  AddIdentityProviderFormNameKey.Name,
  AddIdentityProviderFormNameKey.Realm,
  AddIdentityProviderFormNameKey.ClientId,
  AddIdentityProviderFormNameKey.ClientSecret,
  AddIdentityProviderFormNameKey.WellKnownConfigurationURL,
];

const AdvancedFormKeys = [
  AddIdentityProviderFormNameKey.Name,
  AddIdentityProviderFormNameKey.Realm,
  AddIdentityProviderFormNameKey.ClientId,
  AddIdentityProviderFormNameKey.ClientSecret,
  AddIdentityProviderFormNameKey.IssuerURL,
  AddIdentityProviderFormNameKey.AuthURL,
  AddIdentityProviderFormNameKey.TokenURL,
  AddIdentityProviderFormNameKey.UserInfoURL,
  AddIdentityProviderFormNameKey.JwksURL,
];

type FormFields = Record<AddIdentityProviderFormNameKey, string>;
type FormTouch = Record<AddIdentityProviderFormNameKey, boolean>;

const {
  Name,
  Realm,
  IssuerURL,
  AuthURL,
  TokenURL,
  UserInfoURL,
  JwksURL,
  ClientId,
  ClientSecret,
  MetadataURL,
  WellKnownConfigurationURL,
} = AddIdentityProviderFormNameKey;

const FORM_NAME = 'IdentityProvider';

const defaultFormTouch = Object.values(AddIdentityProviderFormNameKey).reduce((acc, k) => {
  acc[k] = false;
  return acc;
}, {} as FormTouch);

function ProviderLoader({ id }: LoaderProps) {
  useIdentityProvider(id);
  return null;
}

function validateField(name: AddIdentityProviderFormNameKey, value: string) {
  if (!value) return false;
  const maxLen = name === Name ? 255 : 250;
  if (
    [
      IssuerURL,
      AuthURL,
      TokenURL,
      UserInfoURL,
      JwksURL,
      WellKnownConfigurationURL,
      MetadataURL,
    ].includes(name)
  ) {
    return validateUrl(value) && value.length <= maxLen;
  }
  return value.length <= maxLen;
}

function getStatus(name: AddIdentityProviderFormNameKey, value: string, touch: boolean) {
  if (!touch || validateField(name, value)) return undefined;
  return Statuses.error;
}

/**
 * The `@hey-api` client throws the parsed error body (with the HTTP status
 * attached by the shared interceptor, see `src/api/clients/interceptors.ts`).
 * Read the server message and status off that shape so the notification can
 * surface the backend's message.
 */
function getRequestError(e: unknown): { message?: string; status?: number } {
  if (e && typeof e === 'object') {
    const { message, status } = e as { message?: unknown; status?: unknown };
    return {
      message: typeof message === 'string' ? message : undefined,
      status: typeof status === 'number' ? status : undefined,
    };
  }
  return {};
}

function AddEditIdentityProviderModal({
  open,
  onClose,
  identityProvider,
  domains,
  loadedDomains,
}: Props) {
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const { t } = useTranslation('settings');
  const { id } = identityProvider ?? {};
  const isEdit = !!id;
  const initialFields: FormFields = useMemo(() => {
    const { name = '', realm = '', OIDCConfig, SAMLConfig } = identityProvider ?? {};
    const {
      issuerURL = '',
      authURL = '',
      tokenURL = '',
      userInfoURL = '',
      jwksURL = '',
      clientId = '',
      clientSecret = '',
    } = OIDCConfig ?? {};
    const { metaDataURL = '' } = SAMLConfig ?? {};
    return {
      [Name]: name,
      [Realm]: realm,
      [IssuerURL]: issuerURL,
      [AuthURL]: authURL,
      [TokenURL]: tokenURL,
      [UserInfoURL]: userInfoURL,
      [JwksURL]: jwksURL,
      [ClientId]: clientId,
      [ClientSecret]: clientSecret,
      [MetadataURL]: metaDataURL,
      [WellKnownConfigurationURL]: '',
    };
  }, [identityProvider]);

  const prevOpen = useRef(open);
  const { mutateAsync: addIdentityProvider, isPending: isAddIdentityPending } =
    useAddIdentityProvider();
  const { mutateAsync: updateIdentityProvider, isPending: isUpdateIdentityPending } =
    useUpdateIdentityProvider();
  const [formFields, setFormFields] = useState(initialFields);
  const [formTouch, setFormTouch] = useState(defaultFormTouch);
  const [isBasic, setIsBasic] = useState(!isEdit);
  const [identityProviderType, setIdentityProviderType] = useState<'oidc' | 'saml'>(
    !initialFields.metaDataURL.length && isEdit ? 'oidc' : 'saml'
  );

  const isFormValid = Object.values(
    identityProviderType === 'saml' ? SamlFormKeys : isBasic ? BasicFormKeys : AdvancedFormKeys
  ).every((k) => validateField(k, formFields[k]));
  const modalTitle = isEdit ? t('editIdentityProvider') : t('addIdentityProvider');
  const confirmText = isEdit
    ? t('identityProviders.saveChanges')
    : t('identityProviders.addIdentityProvider');

  const notificationTexts = [
    t('identityProviders.addFailure'),
    t('identityProviders.addFailureDesc', { name: formFields[Name] }),
    t('identityProviders.editFailure'),
    t('identityProviders.editFailureDesc', { name: formFields[Name] }),
  ];

  useEffect(() => {
    if (open === prevOpen.current) return;
    prevOpen.current = open;
    if (!open) return;
    resetFormWithName(FORM_NAME);
    setFormFields(initialFields);
    setFormTouch(defaultFormTouch);
  }, [initialFields, open]);

  const handleTextInputChange = (e: TeChangeEvent) => {
    const { target } = e;
    const { value } = target;
    const fieldName = (target as HTMLInputElement).name as AddIdentityProviderFormNameKey;
    const newValue = value.trim();
    if (formFields[fieldName] === newValue) return;
    setFormFields({
      ...formFields,
      [fieldName]: newValue,
    });
    const oldTouch = formTouch[fieldName];
    if (oldTouch) return;
    setFormTouch({ ...formTouch, [fieldName]: true });
  };

  const handleDropdownChange = (e: TeDropdownChangeEvent) => {
    const { value } = e;
    const fieldName = Realm;
    if (formFields[fieldName] === value) return;
    setFormFields({
      ...formFields,
      [fieldName]: value,
    });
    const oldTouch = formTouch[fieldName];
    if (oldTouch) return;
    setFormTouch({ ...formTouch, [fieldName]: true });
  };

  const performOperation = async () => {
    let payload: IdentityProviderPostBody = { name: formFields.name, realm: formFields.realm };
    if (identityProviderType === 'oidc') {
      payload = isBasic
        ? {
            ...payload,
            OIDCConfigWK: {
              [ClientId]: formFields[ClientId],
              [ClientSecret]: formFields[ClientSecret],
              [WellKnownConfigurationURL]: formFields[WellKnownConfigurationURL],
            },
          }
        : {
            ...payload,
            OIDCConfig: {
              [ClientId]: formFields[ClientId],
              [ClientSecret]: formFields[ClientSecret],
              [IssuerURL]: formFields[IssuerURL],
              [AuthURL]: formFields[AuthURL],
              [JwksURL]: formFields[JwksURL],
              [TokenURL]: formFields[TokenURL],
              [UserInfoURL]: formFields[UserInfoURL],
            },
          };
    } else {
      payload = {
        ...payload,
        SAMLConfig: {
          [MetadataURL]: formFields[MetadataURL],
        },
      };
    }

    try {
      if (isEdit) await updateIdentityProvider({ id, payload });
      else await addIdentityProvider(payload);
      return { error: undefined, status: undefined, success: true };
    } catch (e: unknown) {
      if (showError(e)) {
        return { error: undefined, status: undefined, success: false, handled: true };
      }
      const { message, status } = getRequestError(e);
      return { error: message, status, success: false };
    }
  };

  const showFailureNotification = (error?: string) => {
    const operation = isEdit ? 'edit' : 'add';
    const headerKey = `identityProviders.${operation}Failure`;
    const header = t(headerKey);
    const desc = error ? error : t(`${headerKey}Desc`, { name: formFields[Name] });
    if (!notificationTexts.includes(header)) return;
    notificationService.addNotification(header, desc, Statuses.error);
  };

  const handleFormSubmit = async () => {
    // TODO: Add error handling (duplicate name for example throws 409)
    const result = await performOperation();
    if (!result.success && !result.handled) {
      showFailureNotification(result.error);
    }
    if (result.success) {
      onClose(true);
      setFormFields(initialFields);
    }
  };

  const renderField = (
    fieldName: AddIdentityProviderFormNameKey,
    label: string,
    placeholder: string
  ) => {
    const fieldValue = formFields[fieldName];
    const touch = formTouch[fieldName];
    const textInput = (
      <TeInputText
        name={fieldName}
        grow
        size="s"
        placeholder={placeholder}
        required
        status={getStatus(fieldName, fieldValue, touch)}
        valueByDefault={initialFields[fieldName]}
        techange={handleTextInputChange}
      />
    );
    const passwordInput = (
      <TeInputPassword
        name={fieldName}
        grow
        size="s"
        placeholder={placeholder}
        required
        status={getStatus(fieldName, fieldValue, touch)}
        valueByDefault={initialFields[fieldName]}
        techange={handleTextInputChange}
      />
    );
    return (
      <TeContainerSlim gap="xs" column fill>
        <TeLabel text={label} />
        {fieldName === ClientSecret ? passwordInput : textInput}
      </TeContainerSlim>
    );
  };

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      teclose={() => onClose(false)}
      modalTitle={modalTitle}
      contentPadding={0}
      closeable
      size="s"
      width="s"
    >
      {isEdit && <ProviderLoader id={id} />}
      <TeContainer backgroundColor="background.primary" padding={['m', 's']} column grow>
        <TeForm name={FORM_NAME} tesubmit={handleFormSubmit}>
          <TeContainerSlim gap="xs" column fill>
            <TeLabel text={t('realm')} disabled={identityProviderType === 'saml' && isEdit} />
            <TeDropdown
              name={Realm}
              valueByDefault={initialFields.realm}
              grow
              size="s"
              fixedPosition
              placeholderText={t('identityProviders.realmPlaceholder')}
              status={getStatus(Realm, formFields.realm, formTouch.realm)}
              readonly={!loadedDomains}
              techange={handleDropdownChange}
              disabled={identityProviderType === 'saml' && isEdit}
            >
              {domains
                // TODO: Filter away already used domains? Each domain can only be used for one identity provider?
                .filter((domain) => domain.verified)
                .map(({ name }) => {
                  return <TeDropdownOption key={name} value={name} text={name} />;
                })}
            </TeDropdown>
          </TeContainerSlim>

          {renderField(Name, t('identityProviders.name'), t('identityProviders.namePlaceholder'))}
          <TeContainerSlim gap="m" column fill>
            <TeDivider dividerColor="border.secondary" />
            <TeSegment variant="secondary" size="s" grow>
              <TeSegmentText
                text={t('identityProviders.saml')}
                active={identityProviderType === 'saml'}
                teclick={() => setIdentityProviderType('saml')}
                disabled={identityProviderType !== 'saml' && isEdit}
              />
              <TeSegmentText
                text={t('identityProviders.oidc')}
                active={identityProviderType === 'oidc'}
                teclick={() => setIdentityProviderType('oidc')}
                disabled={identityProviderType !== 'oidc' && isEdit}
              />
            </TeSegment>
          </TeContainerSlim>
          {identityProviderType === 'saml' && (
            <TeContainerSlim gap="xs" column fill>
              {renderField(
                MetadataURL,
                t('identityProviders.metadataURL'),
                t('identityProviders.metadataURLPlaceholder')
              )}
            </TeContainerSlim>
          )}
          {identityProviderType === 'oidc' && (
            <TeContainerSlim gap="xs" column fill>
              {renderField(
                ClientId,
                t('identityProviders.clientId'),
                t('identityProviders.clientIdPlaceholder')
              )}
              {renderField(
                ClientSecret,
                t('identityProviders.clientSecret'),
                t('identityProviders.clientSecretPlaceholder')
              )}

              <TeContainerSlim gap="m" padding={['xs', 0, 0]} column fill>
                <TeDivider dividerColor="border.secondary" />
                <TeSegment variant="secondary" size="s" grow>
                  <TeSegmentText
                    text={t('identityProviders.basicSetup')}
                    activeByDefault={isBasic}
                    teclick={() => setIsBasic(true)}
                    disabled={isEdit}
                  />
                  <TeSegmentText
                    text={t('identityProviders.advancedSetup')}
                    activeByDefault={!isBasic}
                    teclick={() => setIsBasic(false)}
                  />
                </TeSegment>

                {isBasic ? (
                  <TeContainerSlim column grow>
                    {renderField(
                      WellKnownConfigurationURL,
                      t('identityProviders.wellKnownConfigurationURL'),
                      t('identityProviders.wellKnownConfigurationURLPlaceholder')
                    )}
                  </TeContainerSlim>
                ) : (
                  <TeContainerSlim column grow gap="m">
                    {renderField(
                      IssuerURL,
                      t('identityProviders.issuerUrl'),
                      t('identityProviders.issuerUrlPlaceholder')
                    )}
                    {renderField(
                      AuthURL,
                      t('identityProviders.authUrl'),
                      t('identityProviders.authUrlPlaceholder')
                    )}
                    {renderField(
                      TokenURL,
                      t('identityProviders.tokenUrl'),
                      t('identityProviders.tokenUrlPlaceholder')
                    )}
                    {renderField(
                      UserInfoURL,
                      t('identityProviders.userInfoUrl'),
                      t('identityProviders.userInfoUrlPlaceholder')
                    )}
                    {renderField(
                      JwksURL,
                      t('identityProviders.jwksUrl'),
                      t('identityProviders.jwksUrlPlaceholder')
                    )}
                  </TeContainerSlim>
                )}
              </TeContainerSlim>
            </TeContainerSlim>
          )}
        </TeForm>
      </TeContainer>

      <TeButton
        slot="secondary"
        text={t('cancel')}
        variant="neutral"
        teclick={() => onClose(false)}
        size="s"
      />
      <TeButton
        slot="primary"
        text={confirmText}
        variant="primary"
        teclick={() => submitFormWithName(FORM_NAME)}
        size="s"
        disabled={!isFormValid || isAddIdentityPending || isUpdateIdentityPending}
        loading={isAddIdentityPending || isUpdateIdentityPending}
      />
    </TeModal>
  );
}

export default AddEditIdentityProviderModal;
